/**
 * @typedef {import('./Contacts/ContactProps').Contact} Contact
 * @typedef {import('./Contacts/ContactProps').ContactList} ContactList
 * @typedef {import('./Contacts/ContactProps').MapContactToRequest} ContactToRequest
 * @typedef {import('./GetContact').default} GetContact
 * @typedef {import('./Contacts/ContactProps').ReturnContactList} ReturnContactList
 * @typedef {import('./Contacts/ContactProps').Source} Source
 */

import apiUrls from '../../config/local/api-urls';
import formatDateForSaving from '../../utils/formatDateForSaving';
import HttpClient from '@mooveguru/js-http-client';

// @ts-ignore it can be ignored
const httpClient = new HttpClient();
/**
 * @typedef {{ id: string; isDefault: boolean; name: string }} ContactTag
 */

/**
 * @typedef {{
 * 	address: {
 * 		city: string | null,
 * 		country: string | null,
 * 		postalCode: string | null,
 * 		state: string | null,
 * 		streetAddress1: string | null,
 * 		streetAddress2: string | null,
 * 	},
 * 	category: string | null,
 * 	details: {
 * 		amountToLoan: number | null,
 * 		amortizationYears?: number | null,
 * 		dueDate: Date | null,
 * 		escrowInsuranceAmount?: number | null,
 * 		escrowInsuranceFrequency?: 'annual' | 'monthly' | null,
 * 		escrowInsuranceMonthly?: number | null,
 * 		escrowTaxAmount?: number | null,
 * 		escrowTaxFrequency?: 'annual' | 'monthly' | null,
 * 		escrowTaxMonthly?: number | null,
 * 		lenderName: string | null,
 * 		loanType: string | null,
 * 		maximumInterestRate: number | null,
 * 		originalDateOfContract: Date | null,
 * 		rate: number | null,
 * 	},
 * 	documents: [],
 * 	id: string,
 * 	isPrimary: boolean,
 * 	title: string,
 * 	valuations: {
 * 		confidence: number | null,
 * 		date: Date | null,
 * 		provider: string,
 * 		valueHigh: number | null,
 * 		valueLow: number | null,
 * 		valueMean: number,
 * 	}[],
 * }} ContactProperty
 */

/**
 * @typedef {{
 * 	id: string;
 * 	activity_type?: string;
 * 	activityType?: string;
 * 	created_at?: string;
 * 	createdAt?: string;
 * 	created_by_user_id?: string | null;
 * 	createdByUserId?: string | null;
 * 	description: string;
 * 	is_system_generated?: boolean;
 * 	isSystemGenerated?: boolean;
 * 	source?: string;
 * 	source_label?: string;
 * 	sourceLabel?: string;
 * }} ContactActivityResponse
 */

/**
 * @param {string} accessToken
 * @returns {Promise<number>}
 */
export async function getNewContacts(accessToken) {
	const response = await httpClient.get(
		apiUrls.group.getContacts,
		new Headers({ 'X-Access-Token': accessToken })
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	return response.body.results.contacts.new;
}

/**
 * @param {string} accessToken
 * @param {{contactId?: string, contactIds?: string[]}} [scope]
 * @returns {Promise<ContactTag[]>}
 */
export async function getContactTags(accessToken, scope = {}) {
	const params = new URLSearchParams();

	if (scope.contactId) {
		params.set('contactId', scope.contactId);
	}

	if (scope.contactIds?.length) {
		params.set('contactIds', scope.contactIds.join(';'));
	}

	const url = params.size
		? `${apiUrls.contacts.tags}?${params.toString()}`
		: apiUrls.contacts.tags;
	const response = await httpClient.get(
		url,
		new Headers({ 'X-Access-Token': accessToken })
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	return (response.body.tags ?? []).map(
		/**
		 * @param {{ id: string; is_default?: boolean; isDefault?: boolean; name: string }} tag
		 * @returns {ContactTag}
		 */
		(tag) => ({
			id: tag.id,
			isDefault: tag.is_default ?? tag.isDefault ?? false,
			name: tag.name,
		})
	);
}

/**
 * @param {string} name
 * @param {string} accessToken
 * @param {{contactId?: string, contactIds?: string[]}} [scope]
 * @returns {Promise<ContactTag>}
 */
export async function createContactTag(name, accessToken, scope = {}) {
	const response = await httpClient.post(
		apiUrls.contacts.tags,
		new Headers({
			'Content-Type': 'application/json',
			'X-Access-Token': accessToken,
		}),
		{ contactId: scope.contactId, name }
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	return {
		id: response.body.id,
		isDefault: false,
		name,
	};
}

/**
 * @param {string} contactId
 * @param {string[]} tagIds
 * @param {string} accessToken
 * @returns {Promise<string[]>}
 */
export async function assignContactTags(contactId, tagIds, accessToken) {
	const response = await httpClient.put(
		`${apiUrls.contacts.view}/${contactId}/tags`,
		new Headers({
			'Content-Type': 'application/json',
			'X-Access-Token': accessToken,
		}),
		{ tagIds }
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	return response.body.tagIds ?? [];
}

/**
 * @param {string[]} contactIds
 * @param {string[]} tagIds
 * @param {string} accessToken
 * @returns {Promise<{contactIds: string[]; tagIds: string[]}>}
 */
export async function bulkAssignContactTags(contactIds, tagIds, accessToken) {
	const response = await httpClient.post(
		`${apiUrls.contacts.view}/bulk/tags`,
		new Headers({
			'Content-Type': 'application/json',
			'X-Access-Token': accessToken,
		}),
		{ contactIds, tagIds }
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	return {
		contactIds: response.body.contactIds ?? [],
		tagIds: response.body.tagIds ?? [],
	};
}

/**
 * @typedef {{
 * 	id: string;
 * 	activityType: string;
 * 	createdAt: string;
 * 	createdByUserId: string | null;
 * 	description: string;
 * 	isSystemGenerated: boolean;
 * 	source: string;
 * 	sourceLabel: string;
 * }} ContactActivity
 */

/**
 * @template T
 * @param {...(T | null | undefined)} values
 * @returns {T | undefined}
 */
function firstDefined(...values) {
	const value = values.find((item) => item !== undefined && item !== null);

	return value === null ? undefined : value;
}

/**
 * @param {ContactActivityResponse} activity
 * @returns {string}
 */
function getContactActivityType(activity) {
	return firstDefined(activity.activityType, activity.activity_type) ?? '';
}

/**
 * @param {ContactActivityResponse} activity
 * @returns {string}
 */
function getContactActivityCreatedAt(activity) {
	return firstDefined(activity.createdAt, activity.created_at) ?? '';
}

/**
 * @param {ContactActivityResponse} activity
 * @returns {string | null}
 */
function getContactActivityCreatedByUserId(activity) {
	return (
		firstDefined(activity.createdByUserId, activity.created_by_user_id) ??
		null
	);
}

/**
 * @param {ContactActivityResponse} activity
 * @returns {boolean}
 */
function getContactActivityIsSystemGenerated(activity) {
	return (
		firstDefined(
			activity.isSystemGenerated,
			activity.is_system_generated
		) ?? false
	);
}

/**
 * @param {ContactActivityResponse} activity
 * @returns {string}
 */
function getContactActivitySourceLabel(activity) {
	return firstDefined(activity.sourceLabel, activity.source_label) ?? '';
}

/**
 * @param {ContactActivityResponse} activity
 * @returns {ContactActivity}
 */
function mapContactActivity(activity) {
	return {
		activityType: getContactActivityType(activity),
		createdAt: getContactActivityCreatedAt(activity),
		createdByUserId: getContactActivityCreatedByUserId(activity),
		description: activity.description,
		id: activity.id,
		isSystemGenerated: getContactActivityIsSystemGenerated(activity),
		source: activity.source ?? '',
		sourceLabel: getContactActivitySourceLabel(activity),
	};
}

/**
 * @param {string} contactId
 * @param {string} accessToken
 * @returns {Promise<ContactActivity[]>}
 */
export async function getContactActivity(contactId, accessToken) {
	const response = await httpClient.get(
		`${apiUrls.contacts.view}/${contactId}/activity`,
		new Headers({ 'X-Access-Token': accessToken })
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	return (response.body.activities ?? []).map(mapContactActivity);
}

/**
 * @param {string} contactId
 * @param {string} description
 * @param {string} accessToken
 * @returns {Promise<string>}
 */
export async function createContactActivity(
	contactId,
	description,
	accessToken
) {
	const response = await httpClient.post(
		`${apiUrls.contacts.view}/${contactId}/activity`,
		new Headers({
			'Content-Type': 'application/json',
			'X-Access-Token': accessToken,
		}),
		{ description }
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	return response.body.id ?? '';
}

/**
 * @param {string} accessToken
 * @returns {Promise<{missing: {address: number, phone: number, email: number}}>}
 */
export async function getMissingContactsData(accessToken) {
	const response = await httpClient.get(
		apiUrls.group.getContacts,
		new Headers({ 'X-Access-Token': accessToken })
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	const results = response.body.results;

	return {
		missing: {
			address: results.missing.address,
			email: results.missing.email,
			phone: results.missing.phone,
		},
	};
}

/**
 * @param {string} accessToken
 * @returns {Promise<Contact[]>}>}
 */
export async function getGroupContacts(accessToken) {
	const response = await httpClient.get(
		apiUrls.me.contacts,
		new Headers({ 'X-Access-Token': accessToken })
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	const contactsList = response.body.contacts;

	return contactsList.map(
		/**
		 * @param {ContactList} contact
		 * @returns {ReturnContactList}
		 */
		(contact) => ({
			address: contact.moving_from,
			email: contact.email_address,
			id: contact.id,
			landline: contact.landline_phone_number,
			leadscore: contact.leadscore,
			mobile: contact.mobile_phone_number,
			moveDate: contact.move_date,
			name: generateFullName(contact.name),
		})
	);
}

/**
 * @param {string} accessToken
 * @returns {Promise<string>}
 */
export async function getAgentId(accessToken) {
	const response = await httpClient.get(
		apiUrls.me.info,
		new Headers({
			'X-Access-Token': accessToken,
		})
	);

	return response.body.results.meta.agent._id;
}

/**
 * @param {Contact} contact
 * @param {Source | null} source
 * @returns {ContactToRequest}
 */
export function mapContactToRequest(contact, source) {
	/* eslint-disable camelcase -- to create as needed data kay */
	const phoneNumberTypes = [
		{ key: 'landline', type: 'home' },
		{ key: 'mobile', type: 'mobile' },
		{ key: 'other', type: 'other' },
		{ key: 'work', type: 'work' },
	];
	const phoneNumbers = phoneNumberTypes
		.map(
			/**
			 * @param {{key: string, type: string}} phoneNumberType
			 * @returns {{ is_primary: boolean, type: string, value: string } | null}
			 */
			(phoneNumberType) => {
				// @ts-ignore -- type is declared
				if (!contact[phoneNumberType.key]) {
					return null;
				}

				return {
					is_primary: phoneNumberType.key === 'mobile',
					type: phoneNumberType.type,
					// @ts-ignore -- type is declared
					value: contact[phoneNumberType.key],
				};
			}
		)
		.filter((phoneNumber) => phoneNumber !== null);

	return {
		address: {
			city: contact.city,
			country: contact.country,
			postal_code: contact.postalCode,
			state: contact.state,
			street_address_1: contact.streetAddress1,
			street_address_2: contact.streetAddress2,
		},
		agent_id: contact.agentId,
		close_date: contact.closeDate
			? formatDateForSaving(contact.closeDate)
			: null,
		email_address: contact.email,
		move_date: contact.moveDate
			? formatDateForSaving(contact.moveDate)
			: null,
		name: {
			first: contact.firstName,
			last: contact.lastName,
		},
		phone_numbers: phoneNumbers,
		source: source ?? undefined,
	};
	/* eslint-enable camelcase */
}

/**
 * @param {Contact} contact
 * @param {string} accessToken
 * @param {Source} source
 * @param {boolean} [sendInvite]
 * @returns {Promise<void>}
 */

export async function addContact(
	contact,
	accessToken,
	source,
	sendInvite = true
) {
	const response = await httpClient.post(
		`${apiUrls.contacts.add}?sendInvite=${sendInvite}`,
		new Headers({
			'Content-Type': 'application/json',
			'X-Access-Token': accessToken,
		}),
		mapContactToRequest(contact, source)
	);

	if (!response.isOk) {
		const error = new Error(response.body);
		// @ts-ignore adding statusCode to error object
		error.statusCode = response.statusCode;
		throw error;
	}
}

/**
 * @param {string} contactId
 * @param {string} accessToken
 * @returns {Promise<GetContact>}
 */

// eslint-disable-next-line complexity
export async function getContact(contactId, accessToken) {
	const response = await httpClient.get(
		`${apiUrls.contacts.view}/${contactId}`,
		new Headers({ 'X-Access-Token': accessToken })
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	const results = response.body;

	return {
		agentId: results.agent_id,
		city: results.address?.city ?? null,
		closeDate: results.close_date,
		country: results.address?.country ?? null,
		createdAt: results.created_at ?? null,
		email: results.email_address,
		firstName: results?.name?.first ?? null,
		homeownerId: results.homeowner_id ?? null,
		id: results.id,
		isBuyer: results.is_buyer ?? false,
		isOwner: results.is_owner ?? false,
		isSubscribedToEmail: results.is_subscribed_to_email ?? false,
		isSubscribedToPhone: results.is_subscribed_to_phone ?? false,
		landline: results.phone_numbers.find(
			/**
			 * @param {{type: string}} phoneNumber
			 * @returns {boolean}
			 */
			(phoneNumber) => ['landline', 'home']?.includes(phoneNumber.type)
		)?.value,
		languagePreference: results.language_preference ?? null,
		lastName: results?.name?.last ?? null,
		mobile: results.phone_numbers.find(
			/**
			 * @param {{type:string}} phoneNumber
			 * @returns {boolean}
			 */
			(phoneNumber) => phoneNumber.type === 'mobile'
		)?.value,
		moveDate: results.move_date,
		other: results.phone_numbers.find(
			/**
			 * @param {{type:string}} phoneNumber
			 * @returns {boolean}
			 */
			(phoneNumber) => phoneNumber.type === 'other'
		)?.value,
		postalCode: results.address?.postal_code ?? null,
		source: results.source ?? null,
		state: results.address?.state ?? null,
		streetAddress1: results.address?.street_address_1 ?? null,
		streetAddress2: results.address?.street_address_2 ?? null,
		tagIds: results.tag_ids ?? [],
		work: results.phone_numbers.find(
			/**
			 * @param {{type:string}} phoneNumber
			 * @returns {boolean}
			 */
			(phoneNumber) => phoneNumber.type === 'work'
		)?.value,
	};
}

/**
 * @param {string} homeownerId
 * @param {string} accessToken
 * @returns {Promise<ContactProperty[]>}
 */
// Response normalization fallbacks are not independent business branches.
// eslint-disable-next-line complexity
export async function getContactHomeownerProperties(homeownerId, accessToken) {
	const response = await httpClient.get(
		`${apiUrls.property['get-property']}/${homeownerId}?includeProperties=true`,
		new Headers({ 'X-Access-Token': accessToken })
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	const results = response.body.results ?? {};
	/** @type {any[]} */
	const fullProperties = Array.isArray(results.properties)
		? results.properties
		: [];
	/** @type {any[]} */
	const propertyReferences = Array.isArray(results.user?.properties)
		? results.user.properties
		: [];
	const primaryProperty = results.property
		? mapContactProperty(results.property)
		: null;

	if (fullProperties.length) {
		return fullProperties.map(mapContactProperty).filter(
			/**
			 * @param {ContactProperty} property
			 * @returns {boolean}
			 */
			(property) => Boolean(property.id)
		);
	}

	if (!propertyReferences.length) {
		return primaryProperty?.id ? [primaryProperty] : [];
	}

	return propertyReferences
		.map(
			/**
			 * @param {any} property
			 * @returns {ContactProperty}
			 */
			(property) => {
				const mappedProperty = mapContactProperty(property);

				if (primaryProperty?.id === mappedProperty.id) {
					return {
						...primaryProperty,
						category: mappedProperty.category,
						isPrimary: mappedProperty.isPrimary,
						title: mappedProperty.title || primaryProperty.title,
					};
				}

				return mappedProperty;
			}
		)
		.filter(
			/**
			 * @param {ContactProperty} property
			 * @returns {boolean}
			 */
			(property) => Boolean(property.id)
		);
}

/**
 * Creates the contact-linked property when needed and returns the mapped property.
 * Repeated requests for the same contact address are idempotent.
 * @param {string} contactId
 * @param {string} accessToken
 * @returns {Promise<ContactProperty>}
 */
export async function createContactProperty(contactId, accessToken) {
	const response = await httpClient.post(
		`${apiUrls.contacts.update}/${contactId}/properties`,
		new Headers({
			'Content-Type': 'application/json',
			'X-Access-Token': accessToken,
		}),
		{}
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}

	return mapContactProperty(response.body);
}

/**
 * @param {{
 * 	address?: {
 * 		city?: string | null,
 * 		country?: string | null,
 * 		postal_code?: string | null,
 * 		postalCode?: string | null,
 * 		state?: string | null,
 * 		street_address_1?: string | null,
 * 		street_address_2?: string | null,
 * 		streetAddress1?: string | null,
 * 		streetAddress2?: string | null,
 * 	},
 * 	category?: string | null,
 * 	details?: {
 * 		amount_to_loan?: number | null,
 * 		amountToLoan?: number | null,
 * 		amortization_years?: number | null,
 * 		amortizationYears?: number | null,
 * 		due_date?: string | Date | null,
 * 		dueDate?: string | Date | null,
 * 		escrow_insurance_amount?: number | null,
 * 		escrowInsuranceAmount?: number | null,
 * 		escrow_insurance_monthly?: number | null,
 * 		escrow_insurance_frequency?: 'annual' | 'monthly' | null,
 * 		escrowInsuranceFrequency?: 'annual' | 'monthly' | null,
 * 		escrowInsuranceMonthly?: number | null,
 * 		escrow_tax_frequency?: 'annual' | 'monthly' | null,
 * 		escrow_tax_monthly?: number | null,
 * 		escrowTaxFrequency?: 'annual' | 'monthly' | null,
 * 		escrowTaxMonthly?: number | null,
 * 		lender_name?: string | null,
 * 		lenderName?: string | null,
 * 		loan_type?: string | null,
 * 		loanType?: string | null,
 * 		maximum_interest_rate?: number | null,
 * 		maximumInterestRate?: number | null,
 * 		original_date_of_contract?: string | Date | null,
 * 		originalDateOfContract?: string | Date | null,
 * 		rate?: number | null,
 * 	},
 * 	_id?: string,
 * 	id?: string,
 * 	is_primary?: boolean,
 * 	isPrimary?: boolean,
 * 	title?: string | null,
 * 	valuations?: {
 * 		confidence?: number | null,
 * 		date?: string | Date | null,
 * 		provider?: string | null,
 * 		value?: {
 * 			high?: number | null,
 * 			low?: number | null,
 * 			mean?: number | null,
 * 		},
 * 	}[],
 * }} property
 * @returns {{
 * 	address: {
 * 		city: string | null,
 * 		country: string | null,
 * 		postalCode: string | null,
 * 		state: string | null,
 * 		streetAddress1: string | null,
 * 		streetAddress2: string | null,
 * 	},
 * 	category: string | null,
 * 	details: ContactProperty['details'],
 * 	documents: [],
 * 	id: string,
 * 	isPrimary: boolean,
 * 	title: string,
 * 	valuations: {
 * 		confidence: number | null,
 * 		date: Date | null,
 * 		provider: string,
 * 		valueHigh: number | null,
 * 		valueLow: number | null,
 * 		valueMean: number,
 * 	}[],
 * }}
 */
// Response normalization fallbacks are not independent business branches.
// eslint-disable-next-line complexity
function mapContactProperty(property) {
	return {
		address: {
			city: property.address?.city ?? null,
			country: property.address?.country ?? null,
			postalCode:
				property.address?.postalCode ??
				property.address?.postal_code ??
				null,
			state: property.address?.state ?? null,
			streetAddress1:
				property.address?.streetAddress1 ??
				property.address?.street_address_1 ??
				null,
			streetAddress2:
				property.address?.streetAddress2 ??
				property.address?.street_address_2 ??
				null,
		},
		category: property.category ?? null,
		details: mapContactPropertyDetails(property.details),
		documents: [],
		id: property.id ?? property._id ?? '',
		isPrimary: property.isPrimary ?? property.is_primary ?? false,
		title: property.title ?? '',
		valuations: Array.isArray(property.valuations)
			? property.valuations.map(mapContactPropertyValuation)
			: [],
	};
}

/**
 * @param {string | Date | null | undefined} value
 * @returns {Date | null}
 */
function mapContactPropertyDate(value) {
	if (!value) {
		return null;
	}

	const date = new Date(value);

	if (Number.isNaN(date.valueOf())) {
		return null;
	}

	return date;
}

/**
 * @param {unknown[]} values
 * @returns {unknown}
 */
function getFirstMeaningfulValue(...values) {
	return values.find(
		(value) => value !== null && value !== undefined && value !== ''
	);
}

/**
 * @param {unknown[]} values
 * @returns {number | null}
 */
function mapContactPropertyNumber(...values) {
	const value = getFirstMeaningfulValue(...values);

	if (value === undefined) {
		return null;
	}

	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : null;
	}

	if (typeof value === 'string') {
		const normalizedValue = Number(value.replace(/[$,%]/g, ''));

		return Number.isFinite(normalizedValue) ? normalizedValue : null;
	}

	return null;
}

/**
 * @param {{
 * 	amount_to_loan?: number | null,
 * 	amountToLoan?: number | null,
 * 	amortization_years?: number | null,
 * 	amortizationYears?: number | null,
 * 	due_date?: string | Date | null,
 * 	dueDate?: string | Date | null,
 * 	escrow_insurance_amount?: number | null,
 * 	escrowInsuranceAmount?: number | null,
 * 	escrow_insurance_frequency?: 'annual' | 'monthly' | null,
 * 	escrowInsuranceFrequency?: 'annual' | 'monthly' | null,
 * 	escrow_insurance_monthly?: number | null,
 * 	escrowInsuranceMonthly?: number | null,
 * 	escrow_tax_amount?: number | null,
 * 	escrowTaxAmount?: number | null,
 * 	escrow_tax_frequency?: 'annual' | 'monthly' | null,
 * 	escrowTaxFrequency?: 'annual' | 'monthly' | null,
 * 	escrow_tax_monthly?: number | null,
 * 	escrowTaxMonthly?: number | null,
 * 	lender_name?: string | null,
 * 	lenderName?: string | null,
 * 	loan_type?: string | null,
 * 	loanType?: string | null,
 * 	maximum_interest_rate?: number | null,
 * 	maximumInterestRate?: number | null,
 * 	original_date_of_contract?: string | Date | null,
 * 	originalDateOfContract?: string | Date | null,
 * 	rate?: number | null,
 * 	mortgage?: {
 * 		mtgAmortizationYears?: number | null,
 * 		mtg_amortization_years?: number | null,
 * 		mtgDueDate?: string | Date | null,
 * 		mtg_due_date?: string | Date | null,
 * 		mtgInterestRate?: number | null,
 * 		mtg_interest_rate?: number | null,
 * 		mtgLenderName?: string | null,
 * 		mtg_lender_name?: string | null,
 * 		mtgLoanAmount?: number | null,
 * 		mtg_loan_amount?: number | null,
 * 		mtgLoanType?: string | null,
 * 		mtg_loan_type?: string | null,
 * 		mtgMaximumInterestRate?: number | null,
 * 		mtg_maximum_interest_rate?: number | null,
 * 		mtgOriginalDateOfContract?: string | Date | null,
 * 		mtg_original_date_of_contract?: string | Date | null,
 * 	},
 * } | null | undefined} details
 * @returns {ContactProperty['details']}
 */
// Response normalization fallbacks are not independent business branches.
// eslint-disable-next-line complexity
function mapContactPropertyDetails(details) {
	const mortgage = details?.mortgage;

	return {
		amortizationYears: mapContactPropertyNumber(
			details?.amortizationYears,
			details?.amortization_years,
			mortgage?.mtgAmortizationYears,
			mortgage?.mtg_amortization_years
		),
		amountToLoan: mapContactPropertyNumber(
			details?.amountToLoan,
			details?.amount_to_loan,
			mortgage?.mtgLoanAmount,
			mortgage?.mtg_loan_amount
		),
		dueDate: mapContactPropertyDate(
			getFirstMeaningfulValue(
				details?.dueDate,
				details?.due_date,
				mortgage?.mtgDueDate,
				mortgage?.mtg_due_date
			)
		),
		escrowInsuranceAmount: mapContactPropertyNumber(
			details?.escrowInsuranceAmount,
			details?.escrow_insurance_amount
		),
		escrowInsuranceFrequency:
			details?.escrowInsuranceFrequency ??
			details?.escrow_insurance_frequency ??
			null,
		escrowInsuranceMonthly: mapContactPropertyNumber(
			details?.escrowInsuranceMonthly,
			details?.escrow_insurance_monthly
		),
		escrowTaxAmount: mapContactPropertyNumber(
			details?.escrowTaxAmount,
			details?.escrow_tax_amount
		),
		escrowTaxFrequency:
			details?.escrowTaxFrequency ??
			details?.escrow_tax_frequency ??
			null,
		escrowTaxMonthly: mapContactPropertyNumber(
			details?.escrowTaxMonthly,
			details?.escrow_tax_monthly
		),
		lenderName:
			details?.lenderName ??
			details?.lender_name ??
			mortgage?.mtgLenderName ??
			mortgage?.mtg_lender_name ??
			null,
		loanType:
			details?.loanType ??
			details?.loan_type ??
			mortgage?.mtgLoanType ??
			mortgage?.mtg_loan_type ??
			null,
		maximumInterestRate:
			details?.maximumInterestRate ??
			details?.maximum_interest_rate ??
			mortgage?.mtgMaximumInterestRate ??
			mortgage?.mtg_maximum_interest_rate ??
			null,
		originalDateOfContract: mapContactPropertyDate(
			getFirstMeaningfulValue(
				details?.originalDateOfContract,
				details?.original_date_of_contract,
				mortgage?.mtgOriginalDateOfContract,
				mortgage?.mtg_original_date_of_contract
			)
		),
		rate: mapContactPropertyNumber(
			details?.rate,
			mortgage?.mtgInterestRate,
			mortgage?.mtg_interest_rate
		),
	};
}

/**
 * @param {{
 * 	confidence?: number | null,
 * 	date?: string | Date | null,
 * 	provider?: string | null,
 * 	value?: {
 * 		high?: number | null,
 * 		low?: number | null,
 * 		mean?: number | null,
 * 	},
 * }} valuation
 * @returns {{
 * 	confidence: number | null,
 * 	date: Date | null,
 * 	provider: string,
 * 	valueHigh: number | null,
 * 	valueLow: number | null,
 * 	valueMean: number,
 * }}
 */
// Response normalization fallbacks are not independent business branches.
// eslint-disable-next-line complexity
function mapContactPropertyValuation(valuation) {
	return {
		confidence: valuation.confidence ?? null,
		date: valuation.date ? new Date(valuation.date) : null,
		provider:
			valuation.provider === 'quantarium_private_label'
				? 'quantariumPrivateLabel'
				: valuation.provider ?? 'quantarium',
		valueHigh: valuation.value?.high ?? null,
		valueLow: valuation.value?.low ?? null,
		valueMean: valuation.value?.mean ?? 0,
	};
}

/**
 * @param {string} id
 * @param {Contact} data
 * @param {string} accessToken
 */
export async function updateContact(id, data, accessToken) {
	const response = await httpClient.put(
		`${apiUrls.contacts.update}/${id}`,
		new Headers({
			'Content-Type': 'application/json',
			'X-Access-Token': accessToken,
		}),
		mapContactToRequest(data, null)
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}
}

/**
 * @param {string} contactId
 * @param {string} propertyId
 * @param {{
 * 	amountToLoan: number | null,
 * 	amortizationYears: number | null,
 * 	escrowInsuranceAmount: number | null,
 * 	escrowInsuranceFrequency: 'annual' | 'monthly',
 * 	escrowInsuranceMonthly: number | null,
 * 	escrowTaxAmount: number | null,
 * 	escrowTaxFrequency: 'annual' | 'monthly',
 * 	escrowTaxMonthly: number | null,
 * 	originalDateOfContract: string | null,
 * 	rate: number | null,
 * }} data
 * @param {string} accessToken
 * @returns {Promise<void>}
 */
export async function updateContactPropertyMortgage(
	contactId,
	propertyId,
	data,
	accessToken
) {
	/** @type {Record<string, number | string | null>} */
	const body = {
		rate: data.rate,
	};
	body['amortization_years'] = data.amortizationYears;
	body['amount_to_loan'] = data.amountToLoan;
	body['escrow_insurance_amount'] = data.escrowInsuranceAmount;
	body['escrow_insurance_frequency'] = data.escrowInsuranceFrequency;
	body['escrow_insurance_monthly'] = data.escrowInsuranceMonthly;
	body['escrow_tax_amount'] = data.escrowTaxAmount;
	body['escrow_tax_frequency'] = data.escrowTaxFrequency;
	body['escrow_tax_monthly'] = data.escrowTaxMonthly;
	body['original_date_of_contract'] = data.originalDateOfContract;

	const response = await httpClient.put(
		`${apiUrls.contacts.update}/${contactId}/properties/${propertyId}/mortgage`,
		new Headers({
			'Content-Type': 'application/json',
			'X-Access-Token': accessToken,
		}),
		body
	);

	if (!response.isOk) {
		throw new Error(response.body.message);
	}
}

/**
 * @param {{first: string, last: string}} name
 * @returns {string|null}
 */

function generateFullName(name) {
	const { first, last } = name;
	if (first && last) {
		return `${first} ${last}`;
	}
	if (first) {
		return first;
	}
	if (last) {
		return last;
	}
	return null;
}
